import axios from "axios";
import { defineStore } from "pinia";
import { requiredHeaders } from "@/helpers/authservice/requiredHeaders";
import { apiRequestsHelper } from "@/state/pinia/apiRequestHelper";
import { log } from "./log";

const userProfileEndpoint = "/api/user-profile";
const userProfileConfigEndpoint = "/api/user-profiles";

export const useUserProfileModule = defineStore("userProfileModule", {
  state: () => ({
    loading: false,
    profile: null,
    avatarObjectURL: null,

    // User Profile Config state
    userProfileConfig: null,
    userProfileSavedFilters: [],
    userProfileSettings: [],
    userProfileViewConfig: null,
  }),

  actions: {
    async httpFetchMyProfile() {
      this.loading = true;
      const endpoint = userProfileEndpoint + "/me";
      console.log("httpFetchMyProfile", endpoint);
      return axios({
        method: "get",
        url: endpoint,
        headers: requiredHeaders(),
      })
        .then((response) => {
          if (response.status === 200) {
            this.profile = response.data.payload;
            if (this.profile && !this.profile.avatarUrl) {
              this.fetchAvatarObjectURL();
            }
            return response.data.payload;
          }
          if (response.status === 203) {
            return apiRequestsHelper.handleResponseWithDialogs(response, false).then(() => {
              throw new Error(response.data.message);
            });
          }
          return apiRequestsHelper.handleResponseWithDialogs(response, false);
        })
        .catch((error) => {
          if (error.response && error.response.status === 203) {
             // Handle 203 if axios throws (though it normally doesn't for 2xx)
             return apiRequestsHelper.handleResponseWithDialogs(error.response, false).then(() => {
                throw new Error(error.response.data.message);
             });
          }
          return apiRequestsHelper.catchResponseError(error, "httpFetchMyProfile")
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async httpSaveMyProfile(updatePayload) {
      this.loading = true;
      const endpoint = `${userProfileEndpoint}/me`;
      // Ensure only editable fields are sent if not already filtered
      const editableFields = ["firstName", "lastName", "phone", "title", "department", "note", "displayName"];
      const filteredPayload = {};
      editableFields.forEach((field) => {
        if (updatePayload[field] !== undefined) {
          filteredPayload[field] = updatePayload[field];
        }
      });

      return axios({
        method: "put",
        url: endpoint,
        headers: requiredHeaders(),
        data: filteredPayload,
      })
        .then((response) => {
          if (response.status === 200) {
            this.profile = response.data.payload;
            return response.data.payload;
          }
          if (response.status === 203) {
            return apiRequestsHelper.handleResponseWithDialogs(response, false).then(() => {
              throw new Error(response.data.message);
            });
          }
          return apiRequestsHelper.handleResponseWithDialogs(response, false);
        })
        .catch((error) => {
          return apiRequestsHelper.catchResponseError(error, "httpSaveMyProfile").then(() => {
            throw error;
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async httpUploadMyAvatar(file) {
      this.loading = true;
      const endpoint = `${userProfileEndpoint}/me/avatar`;
      const fd = new FormData();
      fd.append("file", file);

      const headers = requiredHeaders();
      // Remove Content-Type to let browser set it with boundary for multipart/form-data
      if (headers["Content-Type"]) {
        delete headers["Content-Type"];
      }

      return axios({
        method: "post",
        url: endpoint,
        headers: headers,
        data: fd,
      })
        .then((response) => {
          if (response.status === 200) {
            this.profile = response.data.payload;
            this.fetchAvatarObjectURL();
            return response.data.payload;
          }
          if (response.status === 203) {
            return apiRequestsHelper.handleResponseWithDialogs(response, false).then(() => {
              throw new Error(response.data.message);
            });
          }
          return apiRequestsHelper.handleResponseWithDialogs(response, false);
        })
        .catch((error) => {
          return apiRequestsHelper.catchResponseError(error, "httpUploadMyAvatar").then(() => {
            throw error;
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async httpChangeMyPassword(oldPassword, newPassword) {
      this.loading = true;
      const endpoint = `${userProfileEndpoint}/me/password`;
      return axios({
        method: "put",
        url: endpoint,
        headers: requiredHeaders(),
        data: { oldPassword, newPassword },
      })
        .then((response) => {
          if (response.status === 200) {
            return apiRequestsHelper.handleResponseWithDialogs(response, true).then(() => {
              return true;
            });
          }
          if (response.status === 203) {
            return apiRequestsHelper.handleResponseWithDialogs(response, false).then(() => {
              throw new Error(response.data.message);
            });
          }
          return apiRequestsHelper.handleResponseWithDialogs(response, false);
        })
        .catch((error) => {
          return apiRequestsHelper.catchResponseError(error, "httpChangeMyPassword").then(() => {
            throw error;
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async httpFetchUserProfileByEmailForAdmin(email) {
      const endpoint = `${userProfileEndpoint}/admin/by-email?email=${encodeURIComponent(email)}`;
      return axios({
        method: "get",
        url: endpoint,
        headers: requiredHeaders(),
      })
        .then((response) => {
          if (response.status === 200) {
            return response.data?.payload || response.data;
          }
          return apiRequestsHelper.handleResponseWithDialogs(response, false);
        })
        .catch((error) => {
          return apiRequestsHelper.catchResponseError(error, "httpFetchUserProfileByEmailForAdmin").then(() => {
            throw error;
          });
        });
    },

    async fetchAvatarObjectURL() {
      const endpoint = `${userProfileEndpoint}/me/avatar`;
      return axios({
        method: "get",
        url: endpoint,
        headers: requiredHeaders(),
        responseType: "blob",
      })
        .then((response) => {
          if (response.status === 200) {
            if (this.avatarObjectURL) {
              URL.revokeObjectURL(this.avatarObjectURL);
            }
            this.avatarObjectURL = URL.createObjectURL(response.data);
            return this.avatarObjectURL;
          }
        })
        .catch((error) => {
          log.log("fetchAvatarObjectURL failed", error);
        });
    },

    // ======================= User Profile Config =======================

    _buildConfigQueryParams(email, view, context) {
      let params = "email=" + encodeURIComponent(email);
      if (view) {
        params += "&view=" + encodeURIComponent(view);
      }
      if (context) {
        params += "&context=" + encodeURIComponent(context);
      }
      return params;
    },

    async httpGetUserProfileConfigByEmail(email) {
      const endpoint = userProfileConfigEndpoint + "/config?" + this._buildConfigQueryParams(email);
      return axios({
        method: "get",
        url: endpoint,
        headers: requiredHeaders(),
      })
        .then((response) => {
          if (response.status === 200) {
            this.userProfileConfig = response.data.payload || response.data;
            return this.userProfileConfig;
          }
          return apiRequestsHelper.handleResponseWithDialogs(response, false);
        })
        .catch((error) => {
          return apiRequestsHelper.catchResponseError(error, "httpGetUserProfileConfigByEmail");
        });
    },

    async httpGetUserProfileSavedFilters(email, view, context = null) {
      const endpoint = userProfileConfigEndpoint + "/saved-filters?" + this._buildConfigQueryParams(email, view, context);
      return axios({
        method: "get",
        url: endpoint,
        headers: requiredHeaders(),
      })
        .then((response) => {
          if (response.status === 200) {
            this.userProfileSavedFilters = response.data.payload || response.data || [];
            return this.userProfileSavedFilters;
          }
          return apiRequestsHelper.handleResponseWithDialogs(response, false);
        })
        .catch((error) => {
          return apiRequestsHelper.catchResponseError(error, "httpGetUserProfileSavedFilters");
        });
    },

    async httpSaveUserProfileFilters(email, view, context = null, filters) {
      const endpoint = userProfileConfigEndpoint + "/saved-filters?" + this._buildConfigQueryParams(email, view, context);
      return axios({
        method: "put",
        url: endpoint,
        headers: requiredHeaders(),
        data: filters,
      })
        .then((response) => {
          if (response.status === 200) {
            this.userProfileSavedFilters = response.data.payload || response.data || [];
            return this.userProfileSavedFilters;
          }
          return apiRequestsHelper.handleResponseWithDialogs(response, false);
        })
        .catch((error) => {
          return apiRequestsHelper.catchResponseError(error, "httpSaveUserProfileFilters");
        });
    },

    async httpGetUserProfileSettings(email, view, context = null) {
      const endpoint = userProfileConfigEndpoint + "/settings?" + this._buildConfigQueryParams(email, view, context);
      return axios({
        method: "get",
        url: endpoint,
        headers: requiredHeaders(),
      })
        .then((response) => {
          if (response.status === 200) {
            this.userProfileSettings = response.data.payload || response.data || [];
            return this.userProfileSettings;
          }
          return apiRequestsHelper.handleResponseWithDialogs(response, false);
        })
        .catch((error) => {
          return apiRequestsHelper.catchResponseError(error, "httpGetUserProfileSettings");
        });
    },

    async httpSaveUserProfileSettings(email, view, context = null, settings) {
      const endpoint = userProfileConfigEndpoint + "/settings?" + this._buildConfigQueryParams(email, view, context);
      return axios({
        method: "put",
        url: endpoint,
        headers: requiredHeaders(),
        data: settings,
      })
        .then((response) => {
          if (response.status === 200) {
            this.userProfileSettings = response.data.payload || response.data || [];
            return this.userProfileSettings;
          }
          return apiRequestsHelper.handleResponseWithDialogs(response, false);
        })
        .catch((error) => {
          return apiRequestsHelper.catchResponseError(error, "httpSaveUserProfileSettings");
        });
    },

    async httpGetUserProfileViewConfig(email, view, context = null) {
      const endpoint = userProfileConfigEndpoint + "/view-config?" + this._buildConfigQueryParams(email, view, context);
      return axios({
        method: "get",
        url: endpoint,
        headers: requiredHeaders(),
      })
        .then((response) => {
          if (response.status === 200) {
            const data = response.data.payload || response.data;
            this.userProfileViewConfig = data;
            if (data.config) {
              this.userProfileConfig = data.config;
            }
            if (data.savedFilters) {
              this.userProfileSavedFilters = data.savedFilters;
            }
            if (data.settings) {
              this.userProfileSettings = data.settings;
            }
            return data;
          }
          return apiRequestsHelper.handleResponseWithDialogs(response, false);
        })
        .catch((error) => {
          return apiRequestsHelper.catchResponseError(error, "httpGetUserProfileViewConfig");
        });
    },
  },
});
